import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { chatsApi } from "../../api/chats";
import type { Chat, Project, PromptTemplate } from "../../api/types";
import { projectsApi, sharesApi, templatesApi, type TemplateInput } from "../../api/workspace";

export const useProjects = () => useQuery({ queryKey: ["projects"], queryFn: projectsApi.list, staleTime: 15_000 });

export const useProject = (id: string | undefined) => {
  const q = useProjects();
  return { ...q, project: id ? q.data?.find((p) => p.id === id) : undefined };
};

export function useSaveProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { id?: string; name: string; instructions: string }) =>
      p.id ? projectsApi.update(p.id, { name: p.name, instructions: p.instructions }) : projectsApi.create(p.name, p.instructions),
    onSuccess: (project) =>
      qc.setQueryData<Project[]>(["projects"], (old) => [project, ...(old ?? []).filter((x) => x.id !== project.id)]),
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => projectsApi.remove(id),
    onSuccess: (_r, id) => {
      qc.setQueryData<Project[]>(["projects"], (old) => old?.filter((p) => p.id !== id));
      qc.setQueryData<Chat[]>(["chats"], (old) => old?.map((c) => (c.projectId === id ? { ...c, projectId: null } : c)));
      void qc.invalidateQueries({ queryKey: ["files"] });
    },
  });
}

/** Перенос чата в проект: список чатов меняется сразу, счётчики проектов — после ответа. */
export function useMoveChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ chatId, projectId }: { chatId: string; projectId: string | null }) => chatsApi.move(chatId, projectId),
    onMutate: ({ chatId, projectId }) => {
      const prev = qc.getQueryData<Chat[]>(["chats"]);
      qc.setQueryData<Chat[]>(["chats"], (old) => old?.map((c) => (c.id === chatId ? { ...c, projectId } : c)));
      return { prev };
    },
    onError: (_e, _v, ctx) => ctx?.prev && qc.setQueryData(["chats"], ctx.prev),
    onSettled: () => void qc.invalidateQueries({ queryKey: ["projects"] }),
  });
}

export const useTemplates = () => useQuery({ queryKey: ["templates"], queryFn: templatesApi.list, staleTime: 60_000 });

export function useSaveTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...t }: TemplateInput & { id?: string }) => (id ? templatesApi.update(id, t) : templatesApi.create(t)),
    onSuccess: (t) => qc.setQueryData<PromptTemplate[]>(["templates"], (old) => [t, ...(old ?? []).filter((x) => x.id !== t.id)]),
  });
}

export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => templatesApi.remove(id),
    onSuccess: (_r, id) => qc.setQueryData<PromptTemplate[]>(["templates"], (old) => old?.filter((t) => t.id !== id)),
  });
}

export const useShare = (chatId: string | undefined, enabled: boolean) =>
  useQuery({ queryKey: ["share", chatId], queryFn: () => sharesApi.get(chatId!), enabled: !!chatId && enabled });

export function usePublishShare() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (chatId: string) => sharesApi.publish(chatId),
    onSuccess: (share) => {
      qc.setQueryData(["share", share.chatId], share);
      qc.setQueryData<Chat[]>(["chats"], (old) => old?.map((c) => (c.id === share.chatId ? { ...c, shared: true } : c)));
    },
  });
}

export function useRevokeShare() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (chatId: string) => sharesApi.revoke(chatId),
    onSuccess: (_r, chatId) => {
      qc.setQueryData(["share", chatId], null);
      qc.setQueryData<Chat[]>(["chats"], (old) => old?.map((c) => (c.id === chatId ? { ...c, shared: false } : c)));
    },
  });
}
